import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, FileSpreadsheet } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
          </div>
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Terms of Service Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-ZA')}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-600 mb-4">
                These Terms of Service ("Terms") govern your use of PDF Statement Converter 
                ("Service"), operated by PDF Statement Converter ("us," "we," or "our"). 
                By accessing or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-600">
                If you disagree with any part of these Terms, you may not access the Service. 
                These Terms constitute a legally binding agreement between you and PDF Statement Converter.
              </p>
            </section>

            {/* Service Description */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-600 mb-4">
                PDF Statement Converter provides a digital service that converts PDF bank statements 
                from South African banks into CSV format compatible with accounting software such as 
                Sage, QuickBooks, and Xero.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Supported Banks</h3>
              <p className="text-gray-600 mb-4">
                Our service supports bank statements from major South African banks including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>First National Bank (FNB)</li>
                <li>Standard Bank</li>
                <li>ABSA</li>
                <li>Nedbank</li>
                <li>Capitec Bank</li>
                <li>Other major South African financial institutions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Service Features</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Secure PDF upload and processing</li>
                <li>Automated data extraction and conversion</li>
                <li>CSV output in accounting software-compatible format</li>
                <li>Credit-based pricing system</li>
                <li>24-hour automatic file deletion for security</li>
              </ul>
            </section>

            {/* User Accounts */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-600 mb-4">
                To use our Service, you must create an account by providing accurate and complete 
                information. You are responsible for maintaining the confidentiality of your account 
                credentials and for all activities that occur under your account.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Requirements</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>You must be at least 18 years old to create an account</li>
                <li>You must provide a valid email address</li>
                <li>You must not create multiple accounts</li>
                <li>You must notify us immediately of any unauthorized account use</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Account Termination</h3>
              <p className="text-gray-600">
                You may terminate your account at any time. We reserve the right to suspend or 
                terminate accounts that violate these Terms or engage in fraudulent activity.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use Policy</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Permitted Use</h3>
              <p className="text-gray-600 mb-4">
                You may use our Service only for lawful purposes and in accordance with these Terms. 
                You agree to use the Service only to convert your own bank statements or those for 
                which you have explicit authorization.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Prohibited Activities</h3>
              <p className="text-gray-600 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Upload bank statements that do not belong to you without authorization</li>
                <li>Attempt to reverse engineer or hack our Service</li>
                <li>Use automated systems to access our Service without permission</li>
                <li>Upload malicious files or attempt to compromise system security</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Share your account credentials with others</li>
                <li>Use the Service for money laundering or other illegal financial activities</li>
              </ul>
            </section>

            {/* Payment Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms and Credit System</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Credit System</h3>
              <p className="text-gray-600 mb-4">
                Our Service operates on a credit-based system where credits are consumed for each 
                page of bank statement converted:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li><strong>Pay Per Use:</strong> R2.00 per page</li>
                <li><strong>25 Credit Pack:</strong> R40.00 (R1.60 per page)</li>
                <li><strong>100 Credit Pack:</strong> R140.00 (R1.40 per page)</li>
                <li><strong>Business Plans:</strong> Custom pricing available</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Payment Processing</h3>
              <p className="text-gray-600 mb-4">
                Payments are processed securely through Paystack. We accept major credit cards, 
                debit cards, and other payment methods supported by Paystack in South Africa.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Credit Policies</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-4">
                <li>Credits never expire once purchased</li>
                <li>Credits are non-refundable except as required by law</li>
                <li>Credits are consumed before processing begins</li>
                <li>Failed conversions will result in credit refunds</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.4 Refunds</h3>
              <p className="text-gray-600">
                Refunds are provided only in cases of technical failure or service unavailability. 
                Refunds will be processed within 10 business days. Credits will be refunded 
                automatically for failed conversions due to technical issues.
              </p>
            </section>

            {/* Data and Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Handling and Privacy</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 File Security</h3>
              <p className="text-gray-600 mb-4">
                We implement bank-grade security measures to protect your uploaded files and data. 
                All files are encrypted during transmission and storage.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Automatic Deletion</h3>
              <p className="text-gray-600 mb-4">
                <strong>Important:</strong> All uploaded PDF files and generated CSV files are 
                automatically deleted from our servers after 24 hours. Please download your 
                converted files promptly.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Data Ownership</h3>
              <p className="text-gray-600">
                You retain all ownership rights to your bank statement data. We do not claim 
                ownership of any financial data you upload. Our Privacy Policy governs how we 
                handle your personal information.
              </p>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Service Ownership</h3>
              <p className="text-gray-600 mb-4">
                The Service, including its software, design, content, and functionality, is owned 
                by PDF Statement Converter and protected by copyright, trademark, and other 
                intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 User Content</h3>
              <p className="text-gray-600 mb-4">
                You retain ownership of all bank statements and financial data you upload. 
                By using our Service, you grant us a limited license to process your files 
                solely for the purpose of providing the conversion service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.3 Restrictions</h3>
              <p className="text-gray-600">
                You may not copy, modify, distribute, sell, or lease any part of our Service 
                without our written permission. Reverse engineering is strictly prohibited.
              </p>
            </section>

            {/* Service Availability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Service Availability and Performance</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Uptime Commitment</h3>
              <p className="text-gray-600 mb-4">
                We strive to maintain 99% service availability. However, we do not guarantee 
                uninterrupted service and may experience downtime for maintenance, updates, 
                or unforeseen technical issues.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Maintenance</h3>
              <p className="text-gray-600 mb-4">
                We may perform scheduled maintenance that temporarily affects service availability. 
                We will provide advance notice of planned maintenance when possible.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 Processing Accuracy</h3>
              <p className="text-gray-600">
                While we strive for high accuracy in bank statement conversion, we cannot guarantee 
                100% accuracy. Users should review converted data before using it for accounting 
                or financial purposes.
              </p>
            </section>

            {/* Disclaimers and Warranties */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers and Warranties</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Service Disclaimer</h3>
              <p className="text-gray-600 mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. 
                WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, 
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Data Accuracy</h3>
              <p className="text-gray-600 mb-4">
                We do not warrant that converted data will be error-free or suitable for your 
                specific accounting needs. You are responsible for verifying the accuracy of 
                converted data before use.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.3 Third-Party Services</h3>
              <p className="text-gray-600">
                We are not responsible for the availability, content, or services provided by 
                third parties, including payment processors and cloud service providers.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-600 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PDF STATEMENT CONVERTER SHALL NOT BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR 
                USE OF THE SERVICE.
              </p>
              <p className="text-gray-600 mb-4">
                Our total liability to you for any claims related to the Service shall not exceed 
                the amount you paid for credits in the 12 months preceding the claim.
              </p>
              <p className="text-gray-600">
                This limitation applies regardless of the legal theory on which the claim is based 
                and even if we have been advised of the possibility of such damages.
              </p>
            </section>

            {/* Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-600">
                You agree to indemnify and hold harmless PDF Statement Converter from any claims, 
                damages, losses, or expenses arising from your use of the Service, violation of 
                these Terms, or infringement of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.1 Termination by You</h3>
              <p className="text-gray-600 mb-4">
                You may terminate your account at any time by contacting us. Upon termination, 
                your access to the Service will cease, but unused credits will remain available 
                for 12 months.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.2 Termination by Us</h3>
              <p className="text-gray-600 mb-4">
                We may suspend or terminate your account immediately if you violate these Terms, 
                engage in fraudulent activity, or for other reasonable business reasons.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">12.3 Effect of Termination</h3>
              <p className="text-gray-600">
                Upon termination, all data associated with your account will be deleted according 
                to our data retention policies. Provisions that should survive termination will 
                remain in effect.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law and Disputes</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">13.1 Applicable Law</h3>
              <p className="text-gray-600 mb-4">
                These Terms are governed by the laws of South Africa. Any disputes will be 
                subject to the exclusive jurisdiction of South African courts.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">13.2 Dispute Resolution</h3>
              <p className="text-gray-600">
                We encourage resolving disputes through direct communication. If formal proceedings 
                are necessary, disputes will be resolved through the South African court system.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-600 mb-4">
                We may update these Terms from time to time. Material changes will be communicated 
                via email or through the Service. Your continued use after changes constitutes 
                acceptance of the updated Terms.
              </p>
              <p className="text-gray-600">
                We recommend reviewing these Terms periodically to stay informed of any updates.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
              <p className="text-gray-600 mb-4">
                If you have questions about these Terms or need support, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-2">
                  <strong>Email:</strong> support@pdfstatementconverter.com
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Legal:</strong> legal@pdfstatementconverter.com
                </p>
                <p className="text-gray-600">
                  <strong>Response Time:</strong> We aim to respond within 48 hours during business days
                </p>
              </div>
            </section>

            {/* Severability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Severability and Entire Agreement</h2>
              <p className="text-gray-600 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining 
                provisions will continue in full force and effect.
              </p>
              <p className="text-gray-600">
                These Terms, together with our Privacy Policy, constitute the entire agreement 
                between you and PDF Statement Converter regarding the Service.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">
            © {new Date().getFullYear()} PDF Statement Converter. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;